/**
 * renderSite() -- the whole product, as one function.
 *
 * Returns a complete, self-contained HTML document: inline critical CSS, no
 * framework, no build step, ~15KB. The builder SPA drops the output into an
 * <iframe srcdoc> for live preview and the publisher Lambda writes the same
 * bytes to S3, so preview and production cannot drift.
 *
 * Pre-rendered rather than client-rendered because unfurlers -- X, LinkedIn,
 * Slack, iMessage, Discord -- do not execute JavaScript. A client-rendered
 * page makes every shared link preview identical and wrong.
 */
import { countdownParts } from '@simplelaunch/shared'
import { backgroundBlend, backgroundLayers, scrimLayers, rgba } from './css.js'
import { esc, safeUrl } from './escape.js'
import { FONT_PAIRS, PALETTES, type ThemeChoice } from './theme.js'

export interface Site {
  readonly siteId: string
  readonly slug: string
  readonly name: string
  readonly tagline: string
  /** Absolute launch instant, ISO 8601. Never goes stale in cache. */
  readonly launchAt: string
  /** IANA zone the founder chose the date in, used only for display. */
  readonly timeZone: string
  readonly theme: ThemeChoice
  /** Shown once the countdown reaches zero. */
  readonly liveUrl?: string | undefined
  readonly waitlist: boolean
}

export interface RenderOptions {
  readonly domain?: string
  readonly assetOrigin?: string
  /** Injectable for deterministic tests. */
  readonly now?: Date
}

const DEFAULT_DOMAIN = 'simplelaunch.io'

function formatLaunchDate(launchAt: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(launchAt))
  } catch {
    // An invalid IANA zone must not take the page down.
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(launchAt))
  }
}

/**
 * The countdown, as inline JS.
 *
 * Deliberately not setInterval: it drifts, and browsers throttle it in
 * background tabs, so a returning visitor sees a frozen clock. This aims each
 * timeout at the next second boundary and recomputes on visibilitychange.
 */
function countdownScript(): string {
  return `(function(){
var el=document.getElementById('cd');if(!el)return;
var t=Date.parse(el.getAttribute('data-launch')),tm,
u={d:0,h:0,m:0,s:0};
for(var k in u)u[k]=document.getElementById('cd-'+k);
function p(n){return n<10?'0'+n:''+n}
function done(){
el.setAttribute('data-state','live');
var l=document.getElementById('live');if(l)l.hidden=false;
var w=document.getElementById('pre');if(w)w.hidden=true}
function tick(){
var ms=t-Date.now();
if(!(ms>0)){done();return}
var s=Math.floor(ms/1000);
u.d.textContent=Math.floor(s/86400);
u.h.textContent=p(Math.floor(s/3600)%24);
u.m.textContent=p(Math.floor(s/60)%60);
u.s.textContent=p(s%60);
tm=setTimeout(tick,1000-(Date.now()%1000))}
document.addEventListener('visibilitychange',function(){
if(!document.hidden){clearTimeout(tm);tick()}});
tick()})();`
}

/** Waitlist submission, kept tiny and dependency-free. */
function waitlistScript(): string {
  return `(function(){
var f=document.getElementById('wl');if(!f)return;
f.addEventListener('submit',function(e){
e.preventDefault();
if(f.website.value)return;
var b=f.querySelector('button'),m=document.getElementById('wl-msg');
b.disabled=true;
fetch('/api/waitlist',{method:'POST',headers:{'content-type':'application/json'},
body:JSON.stringify({siteId:f.siteId.value,email:f.email.value,t:Date.now()-f.dataset.loaded})})
.then(function(r){if(!r.ok)throw 0;f.hidden=true;m.hidden=false})
.catch(function(){b.disabled=false;m.hidden=false;m.textContent='Something went wrong. Try again.'})});
f.dataset.loaded=Date.now()})();`
}

function stylesheet(site: Site, assetOrigin: string): string {
  const palette = PALETTES[site.theme.palette]
  const font = FONT_PAIRS[site.theme.font]
  const faces = font.families
    .map(
      (family) => `@font-face{font-family:"${family}";src:url("${assetOrigin}/f/${family}.woff2")format("woff2");font-weight:100 900;font-display:swap}`,
    )
    .join('')

  return `${faces}
*,*::before,*::after{box-sizing:border-box}
html{color-scheme:${palette.scheme}}
body{margin:0;min-height:100svh;display:grid;place-items:center;padding:6vmin 5vmin;
background-color:${palette.bg};color:${palette.text};
font-family:${font.body};font-size:clamp(15px,1.1vw + 12px,18px);line-height:1.55;
-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}
body::before{content:"";position:fixed;inset:0;z-index:-2;
background-image:${backgroundLayers(site.theme.background, palette, site.slug, assetOrigin)};
background-blend-mode:${backgroundBlend(site.theme.background)};
background-size:cover;background-position:center}
body::after{content:"";position:fixed;inset:0;z-index:-1;
background-image:${scrimLayers(site.theme.background, palette)}}
main{width:min(64ch,100%);text-align:center}
h1{margin:0 0 .6rem;font-family:${font.display};font-weight:${font.displayWeight};
font-size:clamp(2.6rem,8vw,4.6rem);line-height:1.02;letter-spacing:${font.displayTracking}}
.tag{margin:0 auto 2.8rem;max-width:44ch;color:${palette.muted};font-size:1.06rem}
.cd{display:flex;gap:clamp(.7rem,3vw,1.8rem);justify-content:center;margin-bottom:2.6rem}
.cd[data-state="live"]{display:none}
.u{display:flex;flex-direction:column;gap:.35rem;min-width:3.6rem}
.n{font-family:${font.display};font-weight:${font.displayWeight};
font-size:clamp(2rem,6vw,3.2rem);line-height:1;color:${palette.accent};
font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1}
.lbl{font-size:.68rem;letter-spacing:.14em;text-transform:uppercase;color:${palette.muted}}
form{display:flex;gap:.5rem;max-width:26rem;margin:0 auto;flex-wrap:wrap}
input[type=email]{flex:1 1 12rem;min-width:0;padding:.72rem .9rem;
border:1px solid ${palette.line};border-radius:.5rem;
background:${rgba(palette.surface, 0.75)};color:${palette.text};font:inherit;font-size:.95rem}
input[type=email]::placeholder{color:${palette.muted}}
input[type=email]:focus-visible,button:focus-visible,a:focus-visible{
outline:2px solid ${palette.accent};outline-offset:2px}
button{padding:.72rem 1.15rem;border:0;border-radius:.5rem;cursor:pointer;
background:${palette.accent};color:${palette.accentText};font:inherit;font-weight:600;font-size:.95rem}
button:disabled{opacity:.6;cursor:progress}
.hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}
.msg{margin:.9rem 0 0;color:${palette.muted};font-size:.92rem}
.live a{color:${palette.accent}}
.badge{display:inline-block;margin-top:3.5rem;color:${palette.muted};opacity:.75;
font-size:.75rem;letter-spacing:.04em;text-decoration:none}
.badge:hover{opacity:1}
.sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;
clip:rect(0 0 0 0);white-space:nowrap;border:0}
@media (prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}`
}

export function renderSite(site: Site, options: RenderOptions = {}): string {
  const domain = options.domain ?? DEFAULT_DOMAIN
  const assetOrigin = options.assetOrigin ?? `https://${domain}`
  const now = options.now ?? new Date()

  const canonical = `https://${site.slug}.${domain}/`
  const ogImage = `${canonical}og.png`
  const launchDate = formatLaunchDate(site.launchAt, site.timeZone)
  const launched = Date.parse(site.launchAt) <= now.getTime()

  // Pre-rendered so there is no blank flash before JS runs. Decomposed with
  // the same floor arithmetic the inline script uses, so the numbers do not
  // visibly change on first paint. Values can be up to an hour stale under
  // s-maxage=3600; JS corrects them within the first frame.
  const parts = countdownParts(new Date(site.launchAt), now)
  const pad = (n: number) => String(n).padStart(2, '0')
  const liveHref = safeUrl(site.liveUrl)

  const title = `${site.name} — launching ${launchDate}`
  const description = site.tagline.trim() || `${site.name} is launching ${launchDate}.`

  const unit = (id: string, value: string, label: string) =>
    `<div class="u"><span class="n" id="cd-${id}">${value}</span><span class="lbl">${label}</span></div>`

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)}</title>
<meta name="description" content="${esc(description)}">
<link rel="canonical" href="${esc(canonical)}">
<meta property="og:type" content="website">
<meta property="og:site_name" content="${esc(site.name)}">
<meta property="og:url" content="${esc(canonical)}">
<meta property="og:title" content="${esc(site.name)}">
<meta property="og:description" content="${esc(description)}">
<meta property="og:image" content="${esc(ogImage)}">
<meta property="og:image:width" content="1200">
<meta property="og:image:height" content="630">
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="${esc(site.name)}">
<meta name="twitter:description" content="${esc(description)}">
<meta name="twitter:image" content="${esc(ogImage)}">
<link rel="icon" href="${esc(assetOrigin)}/favicon.ico">
<style>${stylesheet(site, assetOrigin)}</style>
</head>
<body>
<main>
<h1>${esc(site.name)}</h1>
${site.tagline.trim() ? `<p class="tag">${esc(site.tagline)}</p>` : ''}
<div class="cd" id="cd" data-launch="${esc(site.launchAt)}"${launched ? ' data-state="live"' : ''} aria-hidden="true">
${unit('d', String(parts.days), 'days')}
${unit('h', pad(parts.hours), 'hours')}
${unit('m', pad(parts.minutes), 'mins')}
${unit('s', pad(parts.seconds), 'secs')}
</div>
<p class="sr">Launching on <time datetime="${esc(site.launchAt)}">${esc(launchDate)}</time></p>
<noscript><p class="tag">Launching ${esc(launchDate)}</p></noscript>
<div class="live" id="live"${launched ? '' : ' hidden'}>
<p class="tag">We're live.${liveHref ? ` <a href="${liveHref}">Visit ${esc(site.name)} →</a>` : ''}</p>
</div>
<div id="pre"${launched ? ' hidden' : ''}>
${
  site.waitlist
    ? `<form id="wl" novalidate>
<input type="hidden" name="siteId" value="${esc(site.siteId)}">
<label class="hp" aria-hidden="true">Leave this empty<input type="text" name="website" tabindex="-1" autocomplete="off"></label>
<label class="sr" for="wl-email">Email address</label>
<input id="wl-email" type="email" name="email" required autocomplete="email" placeholder="you@example.com">
<button type="submit">Notify me</button>
</form>
<p class="msg" id="wl-msg" hidden>Thanks — we'll email you at launch.</p>`
    : ''
}
</div>
<a class="badge" href="https://${esc(domain)}/?ref=${esc(site.slug)}">Built with simplelaunch</a>
</main>
<script>${countdownScript()}${site.waitlist ? waitlistScript() : ''}</script>
</body>
</html>`
}
